import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Eye, TrendingUp, Calendar, BarChart3 } from "lucide-react";
import { useArticleAnalytics, useAllArticleViewCounts } from "@/hooks/useArticleViews";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function BlogAnalytics() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { data: analytics } = useArticleAnalytics();
  const { data: allViewCounts } = useAllArticleViewCounts();
  const { data: posts = [] } = useBlogPosts();

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/auth');
        return;
      }

      // Check if user has admin role using RPC to bypass RLS
      const { data: hasAdmin, error: roleError } = await supabase
        .rpc('has_role', { _user_id: user.id, _role: 'admin' });

      if (roleError || !hasAdmin) {
        toast({
          title: "Access Denied",
          description: "You don't have permission to access this page.",
          variant: "destructive",
        });
        navigate('/blog');
        return;
      }

      setIsAdmin(true);
    } catch (error: any) {
      console.error('Error checking admin access:', error);
      navigate('/blog');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAdmin || isLoading) {
    return null;
  }

  // Sort posts by view count
  const postsWithViews = posts
    .map(post => ({
      ...post,
      viewCount: allViewCounts?.[post.id] || 0,
    }))
    .sort((a, b) => b.viewCount - a.viewCount);

  const topPosts = postsWithViews.slice(0, 10);
  const totalViews = analytics?.totalViews || 0;
  const articlesPublished = posts.length;
  const avgViewsPerArticle = articlesPublished > 0 ? Math.round(totalViews / articlesPublished) : 0;

  // Calculate daily views for last 7 days
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    return date.toLocaleDateString();
  });

  const dailyViewsData = last7Days.map(date => ({
    date,
    views: analytics?.dailyViews[date] || 0,
  }));

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Blog Analytics | OdyVoy</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <header className="bg-card/50 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/blog-admin')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Admin
            </Button>
            <h1 className="text-xl font-semibold">Blog Analytics</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Views (30 Days)</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalViews.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Across all articles
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Articles Published</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{articlesPublished}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Total blog posts
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Avg. Views per Article</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{avgViewsPerArticle}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Average engagement
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Daily Views Chart */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Views - Last 7 Days</CardTitle>
            <CardDescription>Daily article view trends</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dailyViewsData.map(({ date, views }) => {
                const maxViews = Math.max(...dailyViewsData.map(d => d.views), 1);
                const percentage = (views / maxViews) * 100;
                
                return (
                  <div key={date} className="flex items-center gap-4">
                    <div className="w-24 text-sm text-muted-foreground">{date}</div>
                    <div className="flex-1 h-8 bg-secondary rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="w-16 text-right text-sm font-medium">{views} views</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Top Articles Table */}
        <Card>
          <CardHeader>
            <CardTitle>Top 10 Articles</CardTitle>
            <CardDescription>Most viewed articles (all time)</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topPosts.map((post, index) => (
                  <TableRow key={post.id}>
                    <TableCell className="font-medium">{index + 1}</TableCell>
                    <TableCell className="font-medium">{post.title}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{post.category}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Eye className="h-4 w-4 text-muted-foreground" />
                        {post.viewCount.toLocaleString()}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
